import { z } from "zod";

/**
 * Job-title screening types shared between the API (which classifies titles against the
 * persona segments and persists the verdicts) and the web app (the Lead Scoring "Job title
 * screening" tab and the XLSX upload title-gate). See docs/prec-3536-job-title-screening.
 */

/** In-persona vs off-persona verdict for a single job title, per workspace. */
export enum JobTitleVerdict {
	WHITELISTED = "WHITELISTED",
	EXCLUDED = "EXCLUDED",
}

/** Where a screening verdict came from. */
export enum JobTitleScreeningSource {
	/** Classified by the AI screener on first sight. */
	AI = "AI",
	/** Added / moved by a user in the Lead Scoring tab. */
	MANUAL = "MANUAL",
	/** Seeded from the Target Persona Segments' jobsToInclude / jobsToExclude. */
	PERSONA_PREFILL = "PERSONA_PREFILL",
}

export const jobTitleVerdictSchema = z.nativeEnum(JobTitleVerdict);
export const jobTitleScreeningSourceSchema = z.nativeEnum(JobTitleScreeningSource);

/** A single persisted screening row as returned to the web app. */
export const jobTitleScreeningSchema = z.object({
	id: z.number(),
	title: z.string(),
	normalizedTitle: z.string(),
	verdict: jobTitleVerdictSchema,
	/** Persona segment this whitelisted title maps to (attribution); null for excluded. */
	personaCategoryId: z.number().nullable(),
	/** Persona segment display name, resolved for the UI; null when unattributed. */
	personaCategory: z.string().nullable(),
	/** AI reasoning (why it fits / why excluded); null for plain manual adds. */
	reasoning: z.string().nullable(),
	source: jobTitleScreeningSourceSchema,
	createdAt: z.string(),
	updatedAt: z.string(),
});

export type JobTitleScreeningItem = z.infer<typeof jobTitleScreeningSchema>;

/** Paginated screening list plus the two per-verdict totals the tab renders. */
export const jobTitleScreeningListSchema = z.object({
	data: z.array(jobTitleScreeningSchema),
	meta: z.object({
		page: z.number(),
		limit: z.number(),
		itemCount: z.coerce.number(),
		pageCount: z.number(),
		hasPreviousPage: z.boolean(),
		hasNextPage: z.boolean(),
	}),
	whitelistCount: z.coerce.number(),
	excludedCount: z.coerce.number(),
});

export type JobTitleScreeningList = z.infer<typeof jobTitleScreeningListSchema>;

/**
 * Dry-run screening preview for the XLSX upload (PREC-3536): how much of a list is already known
 * (free) versus brand-new (AI-screened at 0.1 each). No AI call, no charge.
 */
export const jobTitleScreeningPreviewSchema = z.object({
	/** Total title values submitted (one per row, including blanks and duplicates). */
	totalRows: z.coerce.number(),
	/** Rows whose title was blank (skip the gate, enrich normally). */
	emptyTitles: z.coerce.number(),
	/** Distinct non-blank normalized titles. */
	distinctTitles: z.coerce.number(),
	/** Distinct titles already whitelisted in the workspace (free). */
	alreadyWhitelisted: z.coerce.number(),
	/** Distinct titles already excluded in the workspace (free, skipped). */
	alreadyExcluded: z.coerce.number(),
	/** Distinct titles already in the cache (whitelisted + excluded). */
	analyzedTitles: z.coerce.number(),
	/** Distinct titles not yet in the cache → will be AI-screened. */
	newTitles: z.coerce.number(),
	/** newTitles × 0.1. */
	estimatedScreeningCredits: z.coerce.number(),
});

export type JobTitleScreeningPreview = z.infer<typeof jobTitleScreeningPreviewSchema>;

/** Result of seeding the store from the persona segments. */
export const jobTitleScreeningPrefillResultSchema = z.object({
	whitelisted: z.coerce.number(),
	excluded: z.coerce.number(),
});

export type JobTitleScreeningPrefillResult = z.infer<typeof jobTitleScreeningPrefillResultSchema>;
